import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faBell, 
  faUserCircle, 
  faLock,
  faCheckCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import leadCaptureTracking from '../../services/lead-capture-tracking.service';

interface RightPanelWithLeadCaptureProps {
  sessionId: string;
  onEmailCapture: (email: string) => void;
  hasMessages: boolean;
}

const RightPanelWithLeadCapture: React.FC<RightPanelWithLeadCaptureProps> = ({
  sessionId,
  onEmailCapture,
  hasMessages
}) => {
  const [email, setEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasSignedUp, setHasSignedUp] = useState(false);

  useEffect(() => {
    // Check if user already provided email
    const leadState = leadCaptureTracking.getState();
    if (leadState.emailCaptured) {
      setHasSignedUp(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setError('');
    setIsSubmitting(true);
    
    try {
      await onEmailCapture(email);
      leadCaptureTracking.captureEmail(email);
      setHasSignedUp(true);
      setShowForm(false);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user has already signed up, show minimal version
  if (hasSignedUp) {
    return (
      <div className="p-4 text-center">
        <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
        <h6>You're All Set!</h6>
        <p className="text-muted small">
          We'll notify you when new properties match your search.
        </p>
        <hr />
        <small className="text-muted">
          <FontAwesomeIcon icon={faEnvelope} className="me-1" />
          Alerts sending to: {email || 'your email'}
        </small>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h5 className="mb-4 text-center text-muted">
        {hasMessages ? 'Save Your Search' : (window.location.hostname.includes('aisearchportal') ? 'Your AI-Powered Search' : 'Why chat.realestate?')}
      </h5>

      {!hasMessages ? (
        <>
          {/* Educational content with integrated lead capture */}
          <div className="chat-explanation">
            {/* Existing chat bubbles */}
            <div className="d-flex justify-content-end mb-3">
              <div className="bg-primary text-white rounded-3 px-3 py-2" style={{ maxWidth: '85%', fontSize: '0.9rem' }}>
                How does this work?
              </div>
            </div>

            <div className="d-flex justify-content-start mb-3">
              <div style={{ maxWidth: '85%' }}>
                <div className="bg-white rounded-3 px-3 py-2 shadow-sm" style={{ fontSize: '0.9rem' }}>
                  <div className="mb-1">
                    <span className="me-2">🎯</span>
                    <strong>AI-Powered Home Search</strong>
                  </div>
                  <p className="mb-0 text-muted small">
                    Just describe what you're looking for in your own words. I'll search live MLS listings and find homes that match.
                  </p>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-start mb-3">
              <div style={{ maxWidth: '85%' }}>
                <div className="bg-white rounded-3 px-3 py-2 shadow-sm" style={{ fontSize: '0.9rem' }}>
                  <div className="mb-1">
                    <span className="me-2">🏠</span>
                    <strong>Your Agent's Personal Portal</strong>
                  </div>
                  <p className="mb-0 text-muted small">
                    This search is powered by your agent's expertise and local market knowledge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Integrated CTA */}
          <div className="bg-light rounded-3 p-3 mt-4">
            <div className="text-center mb-3">
              <FontAwesomeIcon icon={faBell} size="2x" className="text-primary mb-2" />
              <h6 className="mb-1">Never Miss Your Dream Home</h6>
              <p className="small text-muted mb-0">
                Get instant alerts when new properties match your criteria
              </p>
            </div>

            {!showForm ? (
              <Button 
                variant="primary" 
                className="w-100"
                onClick={() => setShowForm(true)}
              >
                Get Property Alerts <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
              </Button>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isInvalid={!!error}
                    autoFocus
                  />
                  <Form.Control.Feedback type="invalid">
                    {error}
                  </Form.Control.Feedback>
                </Form.Group>
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Setting up...' : 'Start Getting Alerts'}
                </Button>
              </Form>
            )}

            <div className="text-center mt-2">
              <small className="text-muted">
                <FontAwesomeIcon icon={faLock} className="me-1" />
                No spam, unsubscribe anytime
              </small>
            </div>
          </div>

          <hr className="my-4" />

          {/* Trust indicators */}
          <div className="text-center">
            <small className="text-muted d-block mb-2">Trusted by 50,000+ home buyers</small>
            <div className="d-flex justify-content-center gap-3">
              <small>⚖️ Fair Housing</small>
              <small>🔒 Private</small>
              <small>🤝 Ethical</small>
            </div>
          </div>
        </>
      ) : (
        /* After user starts chatting - more direct lead capture */
        <div className="lead-capture-active">
          <Alert variant="info" className="mb-3">
            <Alert.Heading className="h6">
              <FontAwesomeIcon icon={faBell} className="me-2" />
              Don't Miss New Matches!
            </Alert.Heading>
            <p className="small mb-0">
              Properties are selling fast. Get instant alerts when new homes match your search.
            </p>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="small">Email for property alerts</Form.Label>
              <Form.Control
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={!!error}
              />
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
            </Form.Group>

            <Button variant="primary" type="submit" className="w-100" disabled={isSubmitting}>
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Get Instant Alerts
            </Button>
          </Form>

          <div className="mt-3 p-3 bg-light rounded">
            <h6 className="small mb-2">What you'll get:</h6>
            <ul className="small mb-0 ps-3">
              <li>New properties before they hit Zillow</li>
              <li>Price drop notifications</li>
              <li>Saved search history</li>
              <li>Direct agent connections</li>
            </ul>
          </div>

          <hr className="my-3" />
          
          <div className="text-center">
            <small className="text-muted">
              By signing up, you agree to our{' '}
              <a href="/terms-of-service">Terms</a> and{' '}
              <a href="/privacy-policy">Privacy Policy</a>
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightPanelWithLeadCapture;